import {
	DataSource,
	InfiniteTable,
	type InfiniteTablePropColumns,
} from "@infinite-table/infinite-react";
import type { Holder } from "epsm-api-types/models";
import { useEffect, useState } from "react";
import { getAlign } from "@/lib/utils";
import { settings } from "@/lib/utils/settings/settings";
import {
	formatValueCurrency,
	formatValueDate,
	formatValueNumber,
	formatValuePercent,
} from "../helpers/FormattingHelpers";
import { UseData } from "./UseData";

const getColumns = (
	setColumnOrder: React.Dispatch<React.SetStateAction<string[]>>,
): InfiniteTablePropColumns<Holder> => {
	const columns: InfiniteTablePropColumns<Holder> = {
		owner_name: {
			header: "Owner Name",
			field: "owner_name",
			defaultWidth: 250,
			headerAlign: "start",
			align: getAlign("str"),
		},
		period_ended: {
			header: "Period Ended",
			field: "period_ended",
			type: "date",
			headerAlign: "start",
			align: getAlign("date"),
			valueGetter: ({ data }) => formatValueDate(data.period_ended),
		},
		value: {
			header: "Value",
			field: "value",
			headerAlign: "start",
			align: getAlign("num"),
			valueGetter: ({ data }) => formatValueNumber(data.value),
		},
		amount: {
			header: "Amount",
			field: "amount",
			headerAlign: "start",
			align: getAlign("num"),
			valueGetter: ({ data }) => formatValueCurrency(data.amount),
		},
		amount_percent_change: {
			header: "% Change",
			field: "amount_percent_change",
			headerAlign: "start",
			align: getAlign("num"),
			valueGetter: ({ data }) => formatValuePercent(data.amount_percent_change),
		},
	};

	const columnOrder = Object.keys(columns);
	setColumnOrder(columnOrder);
	return columns;
};

export const HoldersTable = ({ ticker }: { ticker: string }) => {
	const [columnOrder, setColumnOrder] = useState<string[]>([]);
	const [columns, setColumns] = useState<InfiniteTablePropColumns<Holder>>({});
	const [holdersData, setHoldersData] = useState<Holder[]>([]);

	const { execute, isLoading } = UseData.create().useGetTopHoldersData();

	useEffect(() => {
		const fetchData = async () => {
			const result = await execute({
				params: {
					sort: [{ name: "amount", order: -1 }],
					pagination: { limit: 20, offset: 0 },
				},
				ticker,
			});
			setHoldersData(result || []);
		};

		if (ticker) {
			fetchData();
		}
	}, [ticker, execute]);

	useEffect(() => {
		const columnsCall = () => {
			const result = getColumns(setColumnOrder);
			setColumns(result);
		};
		columnsCall();
	}, []);

	return (
		<>
			<div className="text-lg font-semibold p-2">
				<h2>TOP 20 institutional HOLDERS</h2>
			</div>

			<div id="full-h-el" className="flex flex-col h-full">
				<div className="h-full">
					<DataSource<Holder>
						data={holdersData}
						primaryKey="ticker"
						loading={isLoading}
					>
						<InfiniteTable<Holder>
							columns={columns}
							columnOrder={columnOrder}
							onColumnOrderChange={setColumnOrder}
							rowHeight={20}
							licenseKey={settings.infiniteTableLicenseKey}
							columnDefaultFlex={1}
							columnDefaultSortable={false}
							columnDefaultFilterable={false}
						/>
					</DataSource>
				</div>
			</div>
		</>
	);
};
